// Copyright (c) 2019 by Michael Marucheck
//
// Use of this source code is governed by an MIT-style license that
// can be found in the LICENSE file distributed with this file.

import uuidv4 from "uuid/v4";
import React, { useState, useEffect } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import "react-day-picker/lib/style.css";

import { Activity, Supervisor, ReviewState } from "../store/schema";
import { getApi, volCache } from "../store/api";
import {
  encouragement,
  lazy,
  todayString,
  mostCommon,
  logIfErrorA,
  throwIfNull,
  tryHrMinSeconds,
  hrMinFromSeconds,
} from "../util";
import {
  useAsyncGuard,
  update,
  observe,
  useWidthGate,
  Lifecycle,
} from "../UiUtil";
import { URLS, breadcrumbs } from "../urls";
import { CategoryChooser } from "../components/CategoryChooser";
import {
  isFakeOrganizationUid,
  OrganizationChooserDetails,
} from "../components/OrganizationChooser";
import {
  SupervisorChooserDetails,
  isFakeSupervisorUid,
  SupervisorFields,
} from "../components/SupervisorChooser";
import { NavBar, navLink } from "../components/NavBar";
import {
  Concern,
  validate,
  DATE_VALIDATORS,
  UID_VALIDATORS,
  ValidatedDayPicker,
  checkWarn,
  checkErr,
  ValidatedTextAreaDetail,
  validatedDetailClass,
  ValidatedDurationInputDetail,
  HRMIN_VALIDATORS,
} from "../components/ValidatedUI";
import { SaveButton } from "../components/SaveButton";
import { PageContainer } from "../components/PageContainer";
import { DeleteButton } from "../components/DeleteButton";
import { CancelButton } from "../components/CancelButton";
import { Breadcrumbs } from "../components/BreadCrumb";

const WIDTH_GATE = {
  wideNav: 501,
};

const Page: React.FC<RouteComponentProps<{ uid: string }>> = ({
  history,
  match,
}) => {
  debug("======= ActivityDetailPage =======");
  const uid = match.params.uid;
  const widthBucket = useWidthGate(WIDTH_GATE);
  const [state, setState] = useState(volCache().get());
  const [lifecycle, setLifecycle] = useState(Lifecycle.VIEWED);
  const edited = () => setLifecycle(Lifecycle.EDITED);

  const {
    user,
    loaded,
    volunteer,
    school,
    activities,
    activityReviews,
    categories,
    organizations,
    supervisors,
  } = state;
  const activityDoc = activities.find(x => x.data.uid === uid);
  const activity = activityDoc != null ? activityDoc.data : null;
  const [formUid, setFormUid] = useState(uid);

  const initStart = (activity && activity.startDate) || todayString();
  const initEnd = (activity && activity.endDate) || todayString();
  const initHrMin = (activity &&
    activity.durationInSec &&
    hrMinFromSeconds(activity.durationInSec)) || { hours: "", minutes: "00" };
  const initDesc = (activity && activity.description) || "";
  const initCatUid = (activity && activity.categoryUid) || "";
  const initOrgUid = (activity && activity.organizationUid) || "";
  const initSupUid = (activity && activity.supervisorUid) || "";
  const initIsDeleted = (activity && activity.isDeleted) || false;

  const [startDate, setStartDate] = observe(edited, useState(initStart));
  const [endDate, setEndDate] = observe(edited, useState<string>(initEnd));
  const [hours, setHours] = observe(edited, useState(initHrMin.hours));
  const [minutes, setMinutes] = observe(edited, useState(initHrMin.minutes));
  const [description, setDescription] = observe(edited, useState(initDesc));
  const [categoryUid, setCategoryUid] = observe(edited, useState(initCatUid));
  const [organizationUid, setOrganizationUid] = observe(
    edited,
    useState(initOrgUid)
  );
  const [supervisorUid, setSupervisorUid] = observe(
    edited,
    useState(initSupUid)
  );
  const [isDeleted, setIsDeleted] = observe(edited, useState(initIsDeleted));

  if (uid !== formUid) {
    setFormUid(uid);
    setLifecycle(Lifecycle.VIEWED);
    initializeForm(activity || emptyActivity());
  }

  const api = getApi();
  const guard = useAsyncGuard();

  const startWarning = checkWarn<string>(
    date => !!volunteer && date >= `${volunteer.data.classYear - 4}-07-01`,
    "This activity appears to have started before " +
      "your first year in High School " +
      "and is probably not eligible to include here."
  );

  const endWarning = checkWarn<string>(
    date => !!volunteer && date <= `${volunteer.data.classYear}-05-01`,
    "This activity appears to end after the deadline " +
      "of May 1 of your senior year, " +
      "and is probably not eligible to include here."
  );

  const needsDescription = checkErr(
    desc => desc != null && desc !== "",
    "Please describe the nature of your service."
  );

  const concerns: { [key: string]: Concern | null } = {
    startDate: validate([...DATE_VALIDATORS, startWarning], startDate),
    endDate: validate([...DATE_VALIDATORS, endWarning], endDate),
    duration: validate(HRMIN_VALIDATORS, { hours, minutes }),
    categoryUid: validate(UID_VALIDATORS, categoryUid),
    organizationUid: validate(UID_VALIDATORS, organizationUid),
    supervisorUid: validate(UID_VALIDATORS, supervisorUid),
    description: validate([needsDescription], description),
  };

  useEffect(() => {
    return volCache().subscribe(newState => {
      if (update("setState", state, setState, newState)) {
        if (lifecycle !== Lifecycle.EDITED) {
          const aDoc = newState.activities.find(x => x.data.uid === uid);
          const newActivity = aDoc == null ? emptyActivity() : aDoc.data;
          initializeForm(newActivity);
          debug(`state changed, updated form to match new state`);
        } else {
          debug(`state changed but form is edited, ignoring update`);
        }
      } else {
        debug(`volCache notified of update but nothing changed`);
      }
    });
  });

  function emptyActivity(): Activity {
    return {
      uid,
      schoolUid: volunteer == null ? "" : volunteer.data.schoolUid,
      volunteerUid: user == null ? "" : user.uid,
      startDate: todayString(),
      endDate: todayString(),
      durationInSec: 0,
      description: "",
      categoryUid: "",
      organizationUid: "",
      supervisorUid: "",
    };
  }

  function initializeForm(activity: Activity) {
    update("startDate", startDate, setStartDate, activity.startDate);
    update("endDate", endDate, setEndDate, activity.endDate);
    const hrmin = hrMinFromSeconds(activity.durationInSec);
    update(
      "hours",
      hours,
      setHours,
      activity.durationInSec === 0 ? "" : hrmin.hours
    );
    update(
      "minutes",
      minutes,
      setMinutes,
      activity.durationInSec === 0 ? "" : hrmin.minutes
    );
    update("description", description, setDescription, activity.description);
    update("categoryUid", categoryUid, setCategoryUid, activity.categoryUid);
    update(
      "organizationUid",
      organizationUid,
      setOrganizationUid,
      activity.organizationUid
    );
    update(
      "supervisorUid",
      supervisorUid,
      setSupervisorUid,
      activity.supervisorUid
    );
    update("isDeleted", isDeleted, setIsDeleted, activity.isDeleted || false);
    setLifecycle(Lifecycle.VIEWED);
  }

  function onChangeCategory(cuid: string) {
    if (update("categoryUid", categoryUid, setCategoryUid, cuid)) {
      const ouid = mostCommon(
        activities
          .filter(act => act.data.categoryUid === cuid)
          .map(act => act.data.organizationUid)
          .filter(ouid => !isFakeOrganizationUid(ouid))
      );
      if (ouid != null) {
        onChangeOrganization(ouid);
      }
      setCategoryUid(cuid);
    }
  }

  function onChangeOrganization(ouid: string) {
    if (update("organizationUid", organizationUid, setOrganizationUid, ouid)) {
      const suid = mostCommon(
        activities
          .filter(act => act.data.organizationUid === ouid)
          .map(act => act.data.supervisorUid)
          .filter(uid => !isFakeSupervisorUid(uid))
      );
      if (suid != null) {
        setSupervisorUid(suid);
      }
      setOrganizationUid(ouid);
    }
  }

  function onCreateOrganization(uid: string, name: string) {
    return {
      uid,
      schoolUid: throwIfNull(school).data.uid,
      volunteerUid: throwIfNull(user).uid,
      name,
      categoryUid,
    };
  }

  function onCreateSupervisor(
    uid: string,
    fields: SupervisorFields
  ): Supervisor {
    return {
      uid,
      ...fields,
      schoolUid: throwIfNull(school).data.uid,
      volunteerUid: throwIfNull(volunteer).data.uid,
      organizationUid: organizationUid,
    };
  }

  const parseForm = lazy<Activity>(() => {
    return {
      uid,
      schoolUid: school ? school.data.uid : "",
      volunteerUid: throwIfNull(user).uid,
      startDate,
      endDate,
      durationInSec: tryHrMinSeconds({ hours, minutes }) || -1,
      description,
      categoryUid,
      organizationUid,
      supervisorUid,
    };
  });

  async function save() {
    await saveActivity(parseForm());
    history.replace(URLS.vActivitiesDetail(uid));
  }

  async function updateIsDeleted(value: boolean) {
    setIsDeleted(value);
    await saveActivity({ ...parseForm(), isDeleted: value });
  }

  async function saveActivity(activity: Activity) {
    await logIfErrorA(() => guard(api.volunteer.saveActivity(activity)));
  }

  function updateStartDate(date: string) {
    if (startDate === endDate || date > endDate) {
      setEndDate(date);
    }
    setStartDate(date);
  }

  function updateEndDate(date: string) {
    if (date < startDate) {
      setStartDate(date);
    }
    setEndDate(date);
  }

  function statusText() {
    if (activity == null) {
      return null;
    }
    if (isDeleted) {
      return `This activity is deleted.`;
    }

    const review = activityReviews.find(x => x.data.uid === activity.uid);
    const state = review == null ? ReviewState.Pending : review.data.state;

    switch (state) {
      case ReviewState.Pending:
        return (
          `This activity does not yet count towards your approved volunteer time, ` +
          `and is awaiting approval by your coordinator.`
        );

      case ReviewState.Approved:
        return "This activity has been approved by your coordinator.";

      case ReviewState.Rejected:
        return (
          "This activity has been questioned by your coordinator.  " +
          "You may need to discuss it with your coordinator before it can be approved."
        );

      default:
        return "Hmm, I'm confused about the status of this activity.  This is a bug.";
    }

    // switch (activity.status || ActivityStatus.PENDING) {
    //   case ActivityStatus.PENDING:
    //     return (
    //       `This activity does not yet count towards your approved volunteer time, ` +
    //       `and is awaiting approval by your coordinator.`
    //     );
    //   case ActivityStatus.APPROVED:
    //     return `This activity is approved!`;
    //   case ActivityStatus.QUESTIONED:
    //     return (
    //       `Your coordinator has a question or concern about this activity.  ` +
    //       `If you want to keep this activity, please fix and resubmit.`
    //     );
    //   case ActivityStatus.ARCHIVED:
    //     return (
    //       `You archived this activity, ` +
    //       `so it does not count towards your your approved volunteer time.`
    //     );
    //   default:
    //     throw new Error(`INTERNAL ERROR unknown status: ${activity.status}`);
    // }
  }

  return (
    <PageContainer
      title={activityDoc == null ? "Enter New Activity" : "Edit Activity"}
      crumbs={breadcrumbs().vActivities}
      administrators={state.administrators}
      school={state.school}
      checks={{ user, loaded, volunteer }}
    >
      {user && loaded && volunteer && (
        <>
          <div className="section">{statusText()}</div>
          <div className="details-form">
            <div className="details">
              <ValidatedDayPicker
                htmlId="start-date"
                label="Start Date:"
                value={startDate}
                onChange={updateStartDate}
                concern={concerns.startDate}
              />
              <ValidatedDayPicker
                htmlId="end-date"
                label="End Date:"
                value={endDate}
                onChange={updateEndDate}
                concern={concerns.endDate}
              />
              <ValidatedDurationInputDetail
                htmlId="duration"
                label="Time Worked:"
                hoursValue={hours}
                onHoursChange={setHours}
                minutesValue={minutes}
                onMinutesChange={setMinutes}
                concern={concerns.duration}
              />
              <div
                className={validatedDetailClass(false, concerns.categoryUid)}
              >
                <label htmlFor="category">Category:</label>
                <CategoryChooser
                  htmlId="category"
                  categories={categories}
                  uid={categoryUid}
                  onChangeCategory={onChangeCategory}
                />
              </div>
              <OrganizationChooserDetails
                className={validatedDetailClass(
                  false,
                  concerns.organizationUid
                )}
                organizations={organizations}
                organizationUid={organizationUid}
                onChangeOrganization={onChangeOrganization}
                onCreateOrganization={onCreateOrganization}
              />
              <SupervisorChooserDetails
                disabled={isFakeOrganizationUid(organizationUid)}
                supervisors={supervisors}
                organizationUid={organizationUid}
                supervisorUid={supervisorUid}
                className={validatedDetailClass(false, concerns.supervisorUid)}
                onChangeSupervisor={setSupervisorUid}
                onCreateSupervisor={onCreateSupervisor}
              />
              <ValidatedTextAreaDetail
                htmlId="description"
                label="Description:"
                rows={3}
                placeholder={""}
                value={description}
                onChange={setDescription}
                concern={concerns.description}
              />
              <div className="detail">
                <label htmlFor="school">School:</label>
                <input
                  id="school"
                  type="text"
                  value={school == null ? "Loading.." : school.data.name}
                  disabled={true}
                />
              </div>
            </div>
            <div className="done">
              <SaveButton
                lifecycle={lifecycle}
                setLifecycle={setLifecycle}
                concerns={concerns}
                onSave={save}
              />
              <DeleteButton
                isDeleted={isDeleted}
                updateIsDeleted={updateIsDeleted}
                lifecycle={lifecycle}
                setLifecycle={setLifecycle}
              />
              <CancelButton
                lifecycle={lifecycle}
                setLifecycle={setLifecycle}
                onCancel={() => initializeForm(activity || emptyActivity())}
              />
            </div>
            {lifecycle === Lifecycle.SAVED && (
              <>
                <div className="nav">
                  {encouragement()} Here are some ideas for where to go next:
                </div>
                <NavBar
                  wide={widthBucket >= WIDTH_GATE.wideNav}
                  links={[
                    navLink(URLS.index(), "home"),
                    navLink(
                      URLS.vActivitiesDetail(uuidv4()),
                      "Enter New Activity"
                    ),
                    navLink(URLS.vActivitiesAll(), "View All Activities"),
                  ]}
                />
              </>
            )}
          </div>
          {lifecycle === Lifecycle.VIEWED && (
            <>
              <div className="section"></div>
              <Breadcrumbs crumbs={breadcrumbs().vActivities} />
            </>
          )}
        </>
      )}
    </PageContainer>
  );
};
export const ActivityDetailPage = withRouter(Page);

function debug(message: string, state?: any) {
  if (true) {
    console.log(`ActivityDetailPage: ${message}`);
    if (state != null) {
      console.log(state);
    }
  }
}
